import { Check, ImagePlus, RefreshCw, Save, Search, Tag, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import BrandImage from "./BrandImage";
import {
  linkCatalogBrandToProduct,
  loadCatalogProducts,
  productCategoryIds,
  productCategoryLabels,
  type CatalogProduct,
  type ProductCategoryId,
} from "./catalog-data";
import {
  loadManagedBrandSuggestions,
  removeSuggestedBrandImage,
  saveManagedBrandSuggestion,
  uploadSuggestedBrandImage,
  type BrandSuggestionStatus,
  type ManagedBrandSuggestion,
} from "./brand-suggestions";

type Props = { notify: (message: string) => void };

function ReviewCard({ suggestion, products, onSaved, notify }: { suggestion: ManagedBrandSuggestion; products: CatalogProduct[]; onSaved: (value: ManagedBrandSuggestion) => void; notify: Props["notify"] }) {
  const [name, setName] = useState(suggestion.name);
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState<ProductCategoryId | "">("");
  const [productName, setProductName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(suggestion.name);
    setFile(null);
    setCategory("");
    setProductName("");
  }, [suggestion]);

  const categoryProducts = category ? products.filter((product) => product.category === category) : [];
  const selectedProduct = products.find((product) => product.name === productName && product.category === category);

  const commit = async (status: BrandSuggestionStatus) => {
    if (!name.trim()) return;
    if (status === "approved" && (!category || !selectedProduct)) {
      notify("Choose a product category and linked product before approval");
      return;
    }
    setBusy(true);
    let uploaded: { imageUrl: string; storagePath: string } | null = null;
    try {
      if (file) uploaded = await uploadSuggestedBrandImage(suggestion, file);
      const saved = await saveManagedBrandSuggestion(suggestion, {
        name,
        imageUrl: uploaded?.imageUrl ?? suggestion.imageUrl,
        storagePath: uploaded?.storagePath ?? suggestion.storagePath,
        status,
      });
      if (status === "approved" && selectedProduct) {
        await linkCatalogBrandToProduct(saved.name, selectedProduct);
        window.dispatchEvent(new Event("kitanego:catalog-updated"));
      }
      if (uploaded && suggestion.storagePath && suggestion.storagePath !== uploaded.storagePath) await removeSuggestedBrandImage(suggestion.storagePath);
      onSaved(saved);
      setFile(null);
      notify(status === "approved" ? `${saved.name} approved and added to the brand list` : status === "rejected" ? `${saved.name} suggestion rejected` : `${saved.name} suggestion updated`);
    } catch (error) {
      if (uploaded) await removeSuggestedBrandImage(uploaded.storagePath);
      notify(error instanceof Error ? error.message : "Brand suggestion could not be updated");
    } finally {
      setBusy(false);
    }
  };

  return <article className={`brand-suggestion-review brand-suggestion-review--${suggestion.status}`}>
    <div className="brand-suggestion-review__preview">
      {suggestion.imageUrl ? <BrandImage src={suggestion.imageUrl} alt={`${suggestion.name} product`} /> : <Tag aria-hidden="true" />}
    </div>
    <div className="brand-suggestion-review__body">
      <div className="brand-suggestion-review__meta"><span className={`brand-suggestion-status brand-suggestion-status--${suggestion.status}`}>{suggestion.status}</span><small>{suggestion.requestCount} {suggestion.requestCount === 1 ? "request" : "requests"}</small></div>
      <label>Brand name<input value={name} maxLength={60} disabled={busy || suggestion.status === "approved"} onChange={(event) => setName(event.target.value)} /></label>
      <label className={`brand-suggestion-image-input${busy ? " disabled" : ""}`}><ImagePlus size={17} /><span>{file ? file.name : suggestion.imageUrl ? "Replace image" : "Upload image"}</span><input type="file" accept="image/jpeg,image/png,image/webp,image/avif" disabled={busy} onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></label>
      {suggestion.status !== "approved" && <div className="brand-suggestion-link-fields">
        <label>Product category<select value={category} disabled={busy} onChange={(event) => { setCategory(event.target.value as ProductCategoryId | ""); setProductName(""); }}><option value="">Choose category</option>{productCategoryIds.filter((value) => value !== "catalogue").map((value) => <option value={value} key={value}>{productCategoryLabels[value]}</option>)}</select></label>
        <label>Linked product<select value={productName} disabled={busy || !category} onChange={(event) => setProductName(event.target.value)}><option value="">Choose product</option>{categoryProducts.map((product) => <option value={product.name} key={product.name}>{product.name}</option>)}</select></label>
      </div>}
    </div>
    <div className="brand-suggestion-review__actions">
      <button type="button" className="button button--ghost" disabled={busy || !name.trim()} onClick={() => { void commit(suggestion.status); }}><Save size={16} /> {busy ? "Saving…" : "Save"}</button>
      {suggestion.status !== "approved" && <button type="button" className="button button--ink" disabled={busy || !name.trim() || (!file && !suggestion.imageUrl) || !selectedProduct} onClick={() => { void commit("approved"); }}><Check size={16} /> Approve</button>}
      {suggestion.status !== "approved" && suggestion.status !== "rejected" && <button type="button" className="brand-suggestion-reject" disabled={busy} onClick={() => { void commit("rejected"); }}><X size={16} /> Reject</button>}
    </div>
  </article>;
}

export default function BrandSuggestionManager({ notify }: Props) {
  const [suggestions, setSuggestions] = useState<ManagedBrandSuggestion[]>([]);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | BrandSuggestionStatus>("pending");

  const load = async () => {
    setStatus("loading");
    try {
      const [nextSuggestions, nextProducts] = await Promise.all([loadManagedBrandSuggestions(), loadCatalogProducts()]);
      setSuggestions(nextSuggestions);
      setProducts(nextProducts);
      setStatus("ready");
    } catch {
      setStatus("error");
      notify("Brand suggestions could not be loaded");
    }
  };

  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => suggestions.filter((suggestion) =>
    (filter === "all" || suggestion.status === filter)
    && suggestion.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  ), [filter, query, suggestions]);
  const pendingCount = suggestions.filter((suggestion) => suggestion.status === "pending").length;

  return <section className="brand-suggestion-manager" aria-labelledby="brand-suggestion-manager-title">
    <div className="brand-suggestion-manager__head">
      <div><span className="settings-page-card__icon"><Tag /></span><div><h2 id="brand-suggestion-manager-title">Suggested brands</h2><p>Edit the submitted name, add a brand image, assign its product category and linked product, then approve or reject it.</p></div></div>
      <span className="theme-current-pill">{pendingCount} pending</span>
    </div>
    <div className="brand-suggestion-manager__tools">
      <label className="settings-brand-search"><Search /><span className="sr-only">Search suggested brands</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search suggestions" /></label>
      <div className="brand-suggestion-filters" role="group" aria-label="Filter brand suggestions">{(["pending", "approved", "rejected", "all"] as const).map((value) => <button key={value} type="button" className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>{value}</button>)}</div>
      <button type="button" className="brand-suggestion-refresh" disabled={status === "loading"} onClick={() => { void load(); }} aria-label="Refresh brand suggestions"><RefreshCw size={17} /></button>
    </div>
    <div className="brand-suggestion-review-list" aria-live="polite">
      {status === "loading" && <p className="settings-brand-empty">Loading brand suggestions…</p>}
      {status === "error" && <p className="settings-brand-empty">Brand suggestions are temporarily unavailable.</p>}
      {status === "ready" && filtered.map((suggestion) => <ReviewCard key={suggestion.id} suggestion={suggestion} products={products} notify={notify} onSaved={(saved) => setSuggestions((current) => current.map((item) => item.id === saved.id ? saved : item))} />)}
      {status === "ready" && filtered.length === 0 && <p className="settings-brand-empty">No {filter === "all" ? "" : `${filter} `}brand suggestions.</p>}
    </div>
  </section>;
}
